<section class="flex flex-col gap-4 px-4 pt-4 md:px-20 lg:px-40 xl:px-52 xl:pt-8">
    <div class="flex justify-end gap-2 print:hidden">
        <div class="group relative">
            <button
                class="text-primary hover:bg-primary-98 cursor-pointer rounded-md bg-white p-2 text-center shadow-lg transition-colors"
                type="button"
                (click)="copyLink()"
                [attr.aria-label]="'supplier.copy_link' | transloco"
            >
                <svg-icon
                    src="assets/icons/Hyperlink-3.svg"
                    [svgStyle]="{ width: '32px', height: '32px' }"
                    aria-hidden="true"
                />
            </button>
            <div
                class="bg-primary absolute top-full left-1/2 z-50 mt-2 -translate-x-1/2 transform rounded px-3 py-1 text-sm whitespace-nowrap text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                role="tooltip"
            >
                {{ "supplier.copy_link" | transloco }}
            </div>
        </div>

        <div class="group relative">
            <button
                class="text-primary hover:bg-primary-98 cursor-pointer rounded-md bg-white p-2 text-center shadow-lg transition-colors"
                type="button"
                (click)="downloadPdf()"
                [attr.aria-label]="'supplier.download_pdf' | transloco"
            >
                <svg-icon
                    src="assets/icons/Print-Text.svg"
                    [svgStyle]="{ width: '32px', height: '32px' }"
                    class="xl:h-8 xl:w-8"
                    aria-hidden="true"
                />
            </button>
            <div
                class="bg-primary absolute top-full left-1/2 z-50 mt-2 -translate-x-1/2 transform rounded px-3 py-1 text-sm whitespace-nowrap text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                role="tooltip"
            >
                {{ "supplier.download_pdf" | transloco }}
            </div>
        </div>
    </div>

    <div class="flex flex-col gap-4 pb-6 lg:pb-12 xl:flex-row xl:gap-20 print:flex-row print:gap-20">
        <figure class="flex flex-col items-center gap-2 xl:max-w-125 xl:min-w-125 xl:items-start xl:gap-4">
            @if (imageData()) {
                <img
                    [ngSrc]="imageData()!.src | getDirectusImage"
                    [alt]="imageData()!.alt"
                    width="500"
                    height="250"
                    class="xl:max-h-auto mb-2 h-auto w-full max-w-75 object-cover lg:max-w-100 xl:mb-0 xl:max-h-62.5 xl:min-h-62.5 xl:max-w-125 xl:min-w-125 xl:object-contain"
                />
            }

            <div class="flex flex-col items-center justify-center place-self-center">
                <packbo-package-badge
                    [package]="package()"
                    [prefix]="'supplier.member' | transloco"
                    [class.hidden]="package()?.hide_supplier_package"
                />
                <span class="text-headline-medium pt-2 text-center xl:text-center">{{ supplier()?.name }}</span>
            </div>
        </figure>

        <div class="flex w-full flex-col gap-4 xl:w-2/3 xl:gap-6">
            <div class="flex flex-col gap-4 xl:gap-6">
                <p class="text-title-small whitespace-pre-line">
                    {{ supplier()?.translations_refs | getTranslation: "summary" }}
                </p>

                @if (supplier()?.is_eco_friendly) {
                    <div class="bg-custom-2 flex w-fit items-center gap-2 rounded-md px-4 py-3 xl:py-3.5">
                        <svg-icon
                            src="assets/icons/Eco-Friendly.svg"
                            [svgStyle]="{ width: '20px', height: '20px' }"
                            class="xl:h-5 xl:w-6"
                        />
                        <span class="text-body-small xl:text-title-small text-white">{{
                            "supplier.eco_friendly" | transloco
                        }}</span>
                    </div>
                }
            </div>

            <figure class="flex flex-wrap items-center gap-4 pt-4 xl:pt-4">
                @for (certification of certifications(); track certification.id) {
                    <a [href]="certification.website" target="_blank">
                        <img
                            [ngSrc]="certification.logo | getDirectusImage"
                            [alt]="certification.name"
                            width="100"
                            height="100"
                            class="size-20 object-contain xl:max-h-25 xl:min-h-25 xl:max-w-25 xl:min-w-25"
                        />
                    </a>
                }
            </figure>
        </div>
    </div>

    @if (supplier()?.banner) {
        <div class="flex w-full items-center justify-center">
            <figure class="border-neutral-variant-40/20 border-t pb-12 lg:pb-24 print:hidden">
                <img
                    [ngSrc]="supplier()?.banner | getDirectusImage"
                    [alt]="supplier()?.name"
                    width="3000"
                    height="1000"
                    priority
                    class="mt-8 aspect-3/1 max-w-full min-w-full object-fill xl:mt-12 xl:max-h-125"
                />
            </figure>
        </div>
    }

    @if (supplier()?.translations_refs | getTranslation: "description") {
        <div class="flex flex-col gap-4 pb-12">
            <span class="text-title-medium">{{ "supplier.description" | transloco }}</span>
            <p class="text-body-small xl:text-body-small whitespace-pre-line xl:columns-2">
                {{ supplier()?.translations_refs | getTranslation: "description" }}
            </p>
        </div>
    }

    @if (supplierContact() || supplierCoordinates()) {
        <div class="border-neutral-variant-40/20 border-t print:break-before-page">
            <div class="flex flex-col gap-4 pt-8 xl:pt-12">
                <span class="text-title-medium">{{ "supplier.contact_info" | transloco }}</span>
            </div>
        </div>

        @if (supplierCoordinates()) {
            <div
                class="border-primary/20 mb-6 flex flex-col items-start gap-4 rounded-lg border xl:mb-12 xl:flex-row xl:gap-8 print:mb-12 print:flex-row print:gap-8"
            >
                <packbo-supplier-map
                    class="h-64 w-full xl:h-75 xl:w-3/4 print:h-75 print:w-3/4"
                    [coordinate]="supplierCoordinates()"
                />
                <div class="flex w-full flex-col gap-4 p-4 lg:p-0 xl:w-1/4 xl:pt-12 print:w-1/4 print:pt-12">
                    @if (supplierContact()?.first_name || supplierContact()?.last_name) {
                        <div class="text-primary text-body-small flex items-center gap-4">
                            <svg-icon
                                src="assets/icons/Single-Neutral.svg"
                                [svgStyle]="{ width: '24px', height: '24px' }"
                            />
                            <span class="text-neutral-20"
                                >{{ supplierContact()?.first_name }} {{ supplierContact()?.last_name }}</span
                            >
                        </div>
                    }
                    @if (supplierContact()?.phone_number) {
                        <div class="text-primary text-body-small flex items-center gap-4">
                            <svg-icon src="assets/icons/Phone.svg" [svgStyle]="{ width: '24px', height: '24px' }" />
                            <span class="text-neutral-20">{{ supplierContact()?.phone_number | phoneFormat }}</span>
                        </div>
                    }
                    @if (supplierContact()?.email) {
                        <div class="text-primary text-body-small flex items-center gap-4 break-all">
                            <svg-icon
                                src="assets/icons/Email-Action-Unread.svg"
                                [svgStyle]="{ width: '24px', height: '24px' }"
                            />
                            <a
                                [href]="'mailto:' + supplierContact()?.email"
                                class="text-neutral-20 hover:text-primary"
                                >{{ supplierContact()?.email }}</a
                            >
                        </div>
                    }
                    @if (supplier()?.website_url) {
                        <div class="text-primary text-body-small flex items-center gap-4 break-all">
                            <svg-icon
                                src="assets/icons/Hyperlink-3.svg"
                                [svgStyle]="{ width: '24px', height: '24px' }"
                            />
                            <a
                                [href]="supplier()?.website_url"
                                target="_blank"
                                class="text-neutral-20 hover:text-primary"
                                >{{ supplier()?.website_url | urlFormat }}</a
                            >
                        </div>
                    }
                    @if (supplierCoordinates()) {
                        <div class="text-primary text-body-small flex flex-col items-start">
                            <div class="flex items-center gap-4">
                                <svg-icon
                                    src="assets/icons/Maps-Pin-1.svg"
                                    [svgStyle]="{ width: '24px', height: '24px' }"
                                />
                                <span class="text-neutral-20">
                                    {{ supplierCoordinates()?.address1 }},
                                    @if (supplierCoordinates()?.address2) {
                                        {{ supplierCoordinates()?.address2 }},
                                    }
                                </span>
                            </div>
                            <span class="text-neutral-20 pl-10">
                                {{ supplierCoordinates()?.locality }}, {{ supplierCoordinates()?.postal_code }}
                            </span>
                        </div>
                    }
                </div>
            </div>
        }
    }
    <div class="border-neutral-variant-40/20 border-t print:hidden" [class.hidden]="!supplierFiles().length">
        <div class="flex flex-col gap-4 pt-6 pb-4 xl:pt-12">
            <span class="text-title-medium">{{ "supplier.gallery" | transloco }}</span>
        </div>
    </div>
    <figure class="flex flex-wrap items-center justify-around gap-7.5 pb-6 lg:justify-between lg:pb-12 print:hidden">
        @for (file of supplierFiles(); track file; let i = $index) {
            @if (file) {
                <packbo-supplier-files [file]="file" [allImages]="supplierFiles()" [currentImageIndex]="i" />
            }
        }
    </figure>
</section>
